import cn from 'classnames'
import {useField} from 'formik'
import Image from 'next/image'
import {FC} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {RadioButtonMark} from '@/components/common/icons/RadioButtonMark'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {CHECKBOX_TRANSITION, EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'

import {Alert} from '../../alerts/Alert'
import {CheckboxMark} from '../../icons/CheckboxMark'

type FormCardOptionProps = {
  name: string
  value: string | boolean
  title: string
  subtitle?: string
  price?: number
  priceNote?: string
  description?: string
  imageSrc: string
  imageAlt: string
  isSelected: boolean
  onClick?: () => void
  dataTest?: string
  inputType?: 'radio' | 'checkbox'
  isDescriptionVisible?: boolean
}

export const FormCardOption: FC<FormCardOptionProps> = ({
  name,
  value,
  title,
  subtitle,
  price,
  priceNote,
  description,
  imageSrc,
  imageAlt,
  isSelected,
  onClick,
  dataTest,
  inputType = 'radio',
  isDescriptionVisible = false,
}) => {
  const [field, , {setValue}] = useField<string | boolean>(name)
  const isChecked = field.value === value

  return (
    <>
      <div className="item">
        <label
          className={cn('option', isSelected && 'selected')}
          onClick={onClick}
          role="presentation"
        >
          <div className="wrapper" data-test={dataTest}>
            <div className="image">
              <Image
                src={imageSrc}
                alt={imageAlt}
                style={{objectFit: 'contain'}}
                width={40}
                height={40}
                priority
              />
            </div>
            <div>
              <div className="title">
                <Text fontWeight={TextFontWeight.semibold}>{title}</Text>
              </div>
              {subtitle && (
                <div className="subtitle">
                  <Text variant={TextVariant.caption} element="p">
                    {subtitle}
                  </Text>
                </div>
              )}
              {price ? (
                <Text color={theme.palette.text.light}>
                  {price && <LocalizedNumberFormat value={price} type="currency" />}
                  {priceNote}
                </Text>
              ) : null}
            </div>
          </div>
          <input
            type={inputType}
            checked={isChecked}
            onChange={() =>
              inputType === 'radio' ? setValue(value) : setValue(isChecked ? !value : value)
            }
          />
          <div className="mark">
            {inputType === 'radio' ? (
              <RadioButtonMark isChecked={isSelected} />
            ) : (
              <CheckboxMark isChecked={isSelected} />
            )}
          </div>
        </label>
        {description && (
          <div className={cn('description', isDescriptionVisible && 'visible')}>
            <Alert description={description} />
          </div>
        )}
      </div>

      <style jsx>{`
        .item + .item {
          margin-top: ${theme.spacing[3]};
        }
        .option {
          display: inline-block;
          position: relative;
          width: 100%;
          padding: ${theme.spacing[3]};
          border-radius: 12px;
          cursor: pointer;
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          transition: all ${CHECKBOX_TRANSITION}ms ease-in-out;
        }
        .option:hover {
          background-color: ${theme.palette.gray[50]};
        }
        .selected {
          box-shadow: inset 0px 0px 0px 2px ${theme.palette.gray[900]};
        }
        .wrapper {
          display: flex;
          align-items: center;
          user-select: none;
        }
        .image {
          position: relative;
          width: 40px;
          height: 40px;
          flex: 0 0 40px;
          margin-right: ${theme.spacing[3]};
        }
        .title {
          margin-right: ${theme.spacing[5]};
        }
        .subtitle {
          margin-top: ${theme.spacing[1]};
        }
        input {
          position: absolute;
          opacity: 0;
          height: 0;
          width: 0;
        }
        .mark {
          position: absolute;
          top: 16px;
          right: 16px;
        }
        .description {
          opacity: 0;
          visibility: hidden;
          max-height: 0;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out, visibility 0ms;
        }
        .visible {
          opacity: 1;
          visibility: visible;
          max-height: 100px;
          margin-top: ${theme.spacing[3]};
          margin-bottom: ${theme.spacing[4]};
        }
      `}</style>
    </>
  )
}
